import React, { useState, useEffect } from 'react';
import { Play, ArrowRight } from 'lucide-react';
import { portfolioItems, services } from '../data/mock';
import { Button } from '../components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../components/ui/dialog';

const Home = () => {
  const [scrolled, setScrolled] = useState(false);
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [fadeIn, setFadeIn] = useState({});

  useEffect(() => {
    const handleScroll = () => {
      setScrolled(window.scrollY > 50);
      
      // Fade in sections on scroll
      const sections = document.querySelectorAll('.fade-section');
      sections.forEach((section, index) => {
        const rect = section.getBoundingClientRect();
        if (rect.top < window.innerHeight * 0.8) {
          setFadeIn(prev => ({ ...prev, [index]: true }));
        }
      });
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  const scrollToSection = (id) => {
    const element = document.getElementById(id);
    if (element) {
      element.scrollIntoView({ behavior: 'smooth' });
    }
  };

  return (
    <div className="min-h-screen bg-white text-black">
      {/* Navigation */}
      <nav
        className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
          scrolled ? 'bg-white shadow-sm py-3' : 'bg-transparent py-4'
        }`}
      >
        <div className="max-w-7xl mx-auto px-6 flex justify-between items-center">
          <button
            onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
            className="focus:outline-none"
          >
            <img
              src="https://customer-assets.emergentagent.com/job_brand-frame/artifacts/t292b555_Branding%20Frame%20NO%20BACK.png"
              alt="BrandingFrame"
              className="h-8 w-auto object-contain"
            />
          </button>
          <div className="flex gap-8">
            <button
              onClick={() => scrollToSection('services')}
              className="text-sm hover:text-amber-600 transition-colors"
            >
              Services
            </button>
            <button
              onClick={() => scrollToSection('work')}
              className="text-sm hover:text-amber-600 transition-colors"
            >
              Work
            </button>
            <button
              onClick={() => scrollToSection('about')}
              className="text-sm hover:text-amber-600 transition-colors"
            >
              About
            </button>
            <button
              onClick={() => scrollToSection('contact')}
              className="text-sm hover:text-amber-600 transition-colors"
            >
              Contact
            </button>
          </div>
        </div>
      </nav>

      {/* Hero Section */}
      <section className="min-h-screen flex items-center justify-center px-6 pt-20">
        <div className="max-w-5xl text-center">
          <div className="mb-8 animate-fade-in flex justify-center">
            <img
              src="https://customer-assets.emergentagent.com/job_brand-frame/artifacts/t292b555_Branding%20Frame%20NO%20BACK.png"
              alt="BrandingFrame"
              className="h-24 w-auto object-contain"
            />
          </div>
          <p className="text-xl md:text-2xl text-gray-600 mb-12 max-w-3xl mx-auto leading-relaxed animate-fade-in-delay">
            Framing brand stories through modern video, authentic content, and strategic storytelling.
          </p>
          <Button
            onClick={() => scrollToSection('work')}
            className="bg-amber-600 hover:bg-amber-700 text-white px-8 py-6 text-base rounded-sm transition-colors"
          >
            View Our Work
            <ArrowRight className="ml-2 h-4 w-4" />
          </Button>
        </div>
      </section>

      {/* Why BrandingFrame Section */}
      <section
        id="why"
        className="fade-section py-32 px-6 bg-gray-50 opacity-0 transition-opacity duration-1000"
        style={{ opacity: fadeIn[0] ? 1 : 0 }}
      >
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-12">
            Clear storytelling for modern brands
          </h2>
          <div className="space-y-6 text-lg md:text-xl text-gray-700 leading-relaxed">
            <p>Great brands are not just seen.</p>
            <p>They are understood.</p>
            <p>
              BrandingFrame helps companies communicate clearly through thoughtful storytelling and
              modern video production.
            </p>
            <p>Every piece of content should reinforce the brand narrative.</p>
            <p>That is the frame through which we work.</p>
          </div>
        </div>
      </section>

      {/* Services Section */}
      <section
        id="services"
        className="fade-section py-32 px-6 opacity-0 transition-opacity duration-1000"
        style={{ opacity: fadeIn[1] ? 1 : 0 }}
      >
        <div className="max-w-7xl mx-auto">
          <div className="grid md:grid-cols-3 gap-16">
            {services.map((service) => (
              <div key={service.id} className="space-y-4">
                <h3 className="text-2xl font-light tracking-tight">{service.title}</h3>
                <p className="text-gray-600 leading-relaxed">{service.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Work / Portfolio Section */}
      <section
        id="work"
        className="fade-section py-32 px-6 bg-gray-50 opacity-0 transition-opacity duration-1000"
        style={{ opacity: fadeIn[2] ? 1 : 0 }}
      >
        <div className="max-w-7xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-16">Selected Work</h2>
          <div className="grid md:grid-cols-3 gap-8">
            {portfolioItems.map((item) => (
              <div
                key={item.id}
                className="group cursor-pointer"
                onClick={() => setSelectedVideo(item)}
              >
                <div className="relative aspect-video bg-gray-200 mb-4 overflow-hidden">
                  <img
                    src={item.thumbnail}
                    alt={item.title}
                    className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-105"
                  />
                  <div className="absolute inset-0 bg-black bg-opacity-0 group-hover:bg-opacity-20 transition-all duration-300 flex items-center justify-center">
                    <div className="bg-white rounded-full p-4 opacity-0 group-hover:opacity-100 transition-opacity duration-300">
                      <Play className="h-6 w-6 text-black" fill="black" />
                    </div>
                  </div>
                </div>
                <h3 className="text-xl font-light mb-2">{item.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{item.description}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* About Section */}
      <section
        id="about"
        className="fade-section py-32 px-6 opacity-0 transition-opacity duration-1000"
        style={{ opacity: fadeIn[3] ? 1 : 0 }}
      >
        <div className="max-w-4xl mx-auto">
          <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-12">
            About Carlos Rivero
          </h2>
          <div className="flex flex-col md:flex-row gap-8 items-start">
            <div className="md:w-64 flex-shrink-0">
              <img
                src="https://customer-assets.emergentagent.com/job_brand-frame/artifacts/tdhqiwfn_Carlos%20London.png"
                alt="Carlos Rivero"
                className="w-full rounded-sm shadow-lg"
              />
            </div>
            <div className="flex-1 space-y-6">
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
                Carlos Rivero is a creative director and content strategist with decades of experience
                helping brands communicate clearly.
              </p>
              <p className="text-lg md:text-xl text-gray-700 leading-relaxed">
                Through BrandingFrame, he focuses on authentic storytelling, modern video production,
                and strategic brand communication designed for today's platforms.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* Contact Section */}
      <section
        id="contact"
        className="fade-section py-32 px-6 bg-gray-50 opacity-0 transition-opacity duration-1000"
        style={{ opacity: fadeIn[4] ? 1 : 0 }}
      >
        <div className="max-w-4xl mx-auto text-center">
          <h2 className="text-4xl md:text-5xl font-light tracking-tight mb-12">
            Let's frame your brand story
          </h2>
          <div className="space-y-4">
            <div>
              <a
                href="mailto:hello@brandingframe.com"
                className="text-2xl text-amber-600 hover:text-amber-700 transition-colors"
              >
                hello@brandingframe.com
              </a>
            </div>
            <div>
              <a
                href="tel:+12146747093"
                className="text-2xl text-black hover:text-gray-700 transition-colors"
              >
                [214] 674-7093
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Footer */}
      <footer className="py-12 px-6 border-t border-gray-200">
        <div className="max-w-7xl mx-auto">
          <div className="flex justify-between items-center mb-2">
            <div className="text-sm text-gray-500">© 2025 BrandingFrame. All rights reserved.</div>
            <div>
              <img
                src="https://customer-assets.emergentagent.com/job_brand-frame/artifacts/t292b555_Branding%20Frame%20NO%20BACK.png"
                alt="BrandingFrame"
                className="h-6 w-auto object-contain opacity-50"
              />
            </div>
          </div>
          <div>
            <p className="text-xs text-gray-400">
              BrandingFrame is a venture of The Creative Exchange, L.L.C.
            </p>
          </div>
        </div>
      </footer>

      {/* Video Dialog */}
      <Dialog open={!!selectedVideo} onOpenChange={() => setSelectedVideo(null)}>
        <DialogContent className="max-w-4xl">
          <DialogHeader>
            <DialogTitle className="text-2xl font-light">{selectedVideo?.title}</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            {selectedVideo?.videoUrl ? (
              <div className="aspect-video">
                <iframe
                  src={selectedVideo.videoUrl}
                  className="w-full h-full"
                  allowFullScreen
                  title={selectedVideo.title}
                />
              </div>
            ) : (
              <div className="aspect-video bg-gray-100 flex items-center justify-center">
                <div className="text-center">
                  <img
                    src={selectedVideo?.thumbnail}
                    alt={selectedVideo?.title}
                    className="w-full h-full object-cover"
                  />
                </div>
              </div>
            )}
            <div>
              <p className="text-gray-700 leading-relaxed">{selectedVideo?.description}</p>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default Home;
